import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { AuthCoreService } from './auth-core.service';
import { UserManagementService } from './user-management.service';
import { JobTitlesService } from './job-titles.service';
import { InsuranceCompaniesService } from './insurance-companies.service';
import { ProductsService } from './products.service';
import { AuthorizationService } from './authorization.service';
import { UserPreferencesService, PaginatorSettings } from './user-preferences.service';

// Re-export PaginatorSettings for easier imports
export type { PaginatorSettings } from './user-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private authCore: AuthCoreService,
    private userManagement: UserManagementService,
    private jobTitles: JobTitlesService,
    private insuranceCompanies: InsuranceCompaniesService,
    private products: ProductsService,
    private authorization: AuthorizationService,
    private userPreferences: UserPreferencesService
  ) {}

  get isLoggedIn$() {
    return this.authCore.isLoggedIn$;
  }

  get userData$() {
    return this.authCore.userData$;
  }

  login(username: string, password: string) {
    return this.authCore.login(username, password);
  }

  logout(): void {
    this.authCore.logout();
  }

  isAuthenticated(): boolean {
    return this.authCore.isAuthenticated();
  }

  setRedirectUrl(url: string): void {
    this.authCore.setRedirectUrl(url);
  }

  getRedirectUrl(): string {
    return this.authCore.getRedirectUrl();
  }

  getCurrentUser() {
    return this.authCore.getCurrentUser();
  }

  updateUsername(newUsername: string) {
    return this.authCore.updateUsername(newUsername);
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.authCore.changePassword(currentPassword, newPassword);
  }

  // User management
  getAllUsers() {
    return this.userManagement.getAllUsers();
  }

  getCallCenterUsers() {
    return this.userManagement.getCallCenterUsers();
  }

  createUser(userData: any) {
    return this.userManagement.createUser(userData);
  }

  updateUser(userId: number, userData: any) {
    return this.userManagement.updateUser(userId, userData);
  }

  deleteUser(userId: number) {
    return this.userManagement.deleteUser(userId);
  }

  resetPassword(userId: number) {
    return this.userManagement.resetPassword(userId);
  }

  // Job titles
  getAllJobTitles() {
    return this.jobTitles.getAllJobTitles();
  }

  createJobTitle(jobTitle: any) {
    return this.jobTitles.createJobTitle(jobTitle);
  }

  updateJobTitle(jobTitleId: number, jobTitle: any) {
    return this.jobTitles.updateJobTitle(jobTitleId, jobTitle);
  }

  deleteJobTitle(jobTitleId: number) {
    return this.jobTitles.deleteJobTitle(jobTitleId);
  }

  // Insurance companies
  getAllInsuranceCompanies() {
    return this.insuranceCompanies.getAllInsuranceCompanies();
  }

  createInsuranceCompany(insuranceCompany: any) {
    return this.insuranceCompanies.createInsuranceCompany(insuranceCompany);
  }

  updateInsuranceCompany(id: number, insuranceCompany: any) {
    return this.insuranceCompanies.updateInsuranceCompany(id, insuranceCompany);
  }

  deleteInsuranceCompany(id: number) {
    return this.insuranceCompanies.deleteInsuranceCompany(id);
  }

  // Products
  getProductsByCompany(companyId: number) {
    return this.products.getProductsByCompany(companyId);
  }

  createProduct(productData: any) {
    return this.products.createProduct(productData);
  }

  updateProduct(productId: number, productData: any) {
    return this.products.updateProduct(productId, productData);
  }

  deleteProduct(productId: number) {
    return this.products.deleteProduct(productId);
  }

  // Authorization
  getCurrentUserRoles(): string[] {
    return this.authorization.getCurrentUserRoles();
  }

  getCurrentUserRole(): string | null {
    return this.authorization.getCurrentUserRole();
  }

  hasRole(role: string): boolean {
    return this.authorization.hasRole(role as any);
  }

  hasAnyRole(roles: string[]): boolean {
    return this.authorization.hasAnyRole(roles as any);
  }

  isAdmin(): boolean {
    return this.authorization.isAdmin();
  }

  isManager(): boolean {
    return this.authorization.isManager();
  }

  isCallCenterEmployee(): boolean {
    return this.authorization.isCallCenterEmployee();
  }

  isKAM(): boolean {
    return this.authorization.isKAM();
  }

  isInvestmentDivision(): boolean {
    return this.authorization.isInvestmentDivision();
  }

  isSalesSupportManager(): boolean {
    return this.authorization.isSalesSupportManager();
  }

  // User Preferences
  /**
   * Felhasználó paginator beállításainak lekérdezése
   * @param pageName Oldal azonosító (pl. 'call_center_database')
   */
  getPaginatorSettings(pageName: string): Observable<PaginatorSettings> {
    return this.userPreferences.getPaginatorSettings(pageName);
  }

  /**
   * Paginator beállítások mentése
   * @param pageName Oldal azonosító
   * @param settings Paginator beállítások
   */
  savePaginatorSettings(pageName: string, settings: PaginatorSettings): Observable<any> {
    return this.userPreferences.savePaginatorSettings(pageName, settings);
  }

  /**
   * Paginator beállítások törlése
   * @param pageName Oldal azonosító
   */
  deletePaginatorSettings(pageName: string): Observable<any> {
    return this.userPreferences.deletePaginatorSettings(pageName);
  }

  /**
   * Összes felhasználói preferencia lekérdezése
   */
  getAllPreferences(): Observable<any> {
    return this.userPreferences.getAllPreferences();
  }
}
